import posthog from "posthog-js";
import type { ConnectedEquipment } from "verdi-common/schemas/device/device";
import { INFO_CARD_TYPE } from "@/features/cards/shared/card-routing/card-types";
import type { ColumnGrouping } from "@/pages/data-export/types";
import type { OPERATING_MODE } from "@/services/device-attributes/attributes/operating-mode/definitions";
import { posthogEnabled } from "./enabled";

/**
 * PostHog event catalog — names and property shapes live here so call sites can't drift.
 *
 * Event names: `category:object_action`, lowercase, present tense, fixed strings only.
 * Put variation in properties, not parallel event names.
 * Omit unknown values (`undefined`) — never send `""`, which pollutes breakdowns.
 */

export type IrrigationCreationMethod = "on_demand" | "scheduler";
export type AnalyticsErrorCode = "api_failed" | "validation" | "registration_failed" | "unknown";
export type CardEntityType = "device" | "zone" | "aoi";
export type DurationSource = "preset" | "custom";
export type RecurrenceFrequency = "daily" | "weekly" | "monthly" | "yearly";
/** Breakdown-friendly duration bins. Keep `duration_ms` alongside for averages. */
export type DurationBucket = "lte_15m" | "lte_1h" | "lte_4h" | "gt_4h";

/**
 * Shared device fields for device-scoped events. Build with buildDeviceProperties.
 * `verdi_id` is the human lookup id (e.g. "D-MUF-338"); `device_version_code` is e.g. "4.block.2.a.3".
 */
export type DeviceProperties = {
    device_id: string;
    verdi_id?: string;
    device_version_code?: string;
};

/**
 * Shared zone fields for irrigation events. Build with buildZoneProperties.
 * `zone_ids` is a list so PostHog can break down / filter per zone; `zone_count` is for volume math.
 */
export type ZoneProperties = { zone_ids: string[]; zone_count: number };

/**
 * Shared recurrence fields for scheduler irrigation events. Build with buildRecurrenceProperties.
 * Frequency / weekday count are only set when the rule is actually recurring.
 */
export type RecurrenceProperties = {
    is_recurring: boolean;
    recurrence_frequency?: RecurrenceFrequency;
    days_of_week_count?: number;
};

/** Event name constants — wire strings live only here. */
export const ANALYTICS_EVENT = {
    USER_LOGIN: "auth:user_login",

    // Card events
    CARD_OPEN: "ui:card_open",
    CARD_CLOSE: "ui:card_close",

    // Data Export
    DATA_EXPORT: "data:export",

    // Device Card
    DEVICE_OPERATING_MODE_CHANGE: "device:operating_mode_change",
    SENSOR_SETTINGS_SAVE: "device:sensor_settings_save",
    EQUIPMENT_SETTINGS_SAVE: "device:equipment_settings_save",
    EQUIPMENT_SETTINGS_SAVE_FAIL: "device:equipment_settings_save_fail",
    DEVICE_DELETE: "device:delete",
    DEVICE_REPOSITION: "device:reposition",
    DEVICE_MULTI_DEPTH_VIEW_OPEN: "device:multi_depth_view_open",

    // Irrigation Events (regardless of creation method)
    IRRIGATION_EVENT_CREATE: "irrigation:event_create",
    IRRIGATION_EVENT_CREATE_FAIL: "irrigation:event_create_fail",
    IRRIGATION_EVENT_DELETE: "irrigation:event_delete",
    IRRIGATION_EVENT_UPDATE: "irrigation:event_update",
    IRRIGATION_EVENT_UPDATE_FAIL: "irrigation:event_update_fail",

    // Scheduler Card
    IRRIGATION_SET_SAVE: "irrigation:set_save",
    IRRIGATION_SET_SAVE_FAIL: "irrigation:set_save_fail",

    // Zone Card
    ZONE_EDIT_CLICK: "zone:edit_click",

    // Add Device
    DEVICE_REGISTRATION_COMPLETE: "device:registration_complete",
    DEVICE_REGISTRATION_FAIL: "device:registration_fail",

    // Frill Release
    RELEASE_ANNOUNCEMENT_OPEN: "ui:release_announcement_open",
    RELEASE_ANNOUNCEMENT_CLOSE: "ui:release_announcement_close",

    // Irrigation Report
    IRRIGATION_REPORT_DOWNLOAD: "irrigation:report_download",
} as const;

/**
 * Property shape for each event. Keys follow ANALYTICS_EVENT — add a constant above and a matching entry here.
 */
export type AnalyticsEventProperties = {
    // No properties for this event - identity is handled by identifyUser
    [ANALYTICS_EVENT.USER_LOGIN]: Record<string, never>;

    // Cards
    [ANALYTICS_EVENT.CARD_OPEN]: {
        card_type: INFO_CARD_TYPE;
        entity_id?: string;
        entity_type?: CardEntityType;
    };
    [ANALYTICS_EVENT.CARD_CLOSE]: {
        card_type: INFO_CARD_TYPE;
        entity_id?: string;
        entity_type?: CardEntityType;
        open_duration_ms?: number;
    };

    // Data export
    [ANALYTICS_EVENT.DATA_EXPORT]: {
        source_count: number;
        data_type_count: number;
        date_range_days: number;
        column_grouping: ColumnGrouping;
        split_by_position_depth: boolean;
        data_types: string[];
    };

    // Device
    [ANALYTICS_EVENT.DEVICE_OPERATING_MODE_CHANGE]: DeviceProperties & {
        new_mode: OPERATING_MODE;
        previous_mode: OPERATING_MODE;
        connected_equipment: ConnectedEquipment;
        is_device_unreachable: boolean;
    };
    [ANALYTICS_EVENT.SENSOR_SETTINGS_SAVE]: DeviceProperties & { sensor_type?: string };
    [ANALYTICS_EVENT.EQUIPMENT_SETTINGS_SAVE]: DeviceProperties & {
        valve_index: number;
        connected_zone_count: number;
        // Block devices only (valve vs pump); omit otherwise.
        connected_equipment_type?: ConnectedEquipment;
    };
    [ANALYTICS_EVENT.EQUIPMENT_SETTINGS_SAVE_FAIL]: DeviceProperties & {
        valve_index: number;
        error_code: AnalyticsErrorCode;
    };
    [ANALYTICS_EVENT.DEVICE_DELETE]: DeviceProperties;
    [ANALYTICS_EVENT.DEVICE_REPOSITION]: DeviceProperties;
    [ANALYTICS_EVENT.DEVICE_MULTI_DEPTH_VIEW_OPEN]: DeviceProperties;

    // Irrigation (on-demand and scheduler share these)
    [ANALYTICS_EVENT.IRRIGATION_EVENT_CREATE]: ZoneProperties &
        Partial<RecurrenceProperties> & {
            event_id: string;
            duration_ms: number;
            duration_bucket: DurationBucket;
            is_scheduled_start: boolean;
            creation_method: IrrigationCreationMethod;
            duration_source?: DurationSource;
            // Set when created from / linked to an irrigation set.
            irrigation_set_id?: string;
        };
    [ANALYTICS_EVENT.IRRIGATION_EVENT_CREATE_FAIL]: ZoneProperties & {
        creation_method: IrrigationCreationMethod;
        error_code: AnalyticsErrorCode;
    };
    [ANALYTICS_EVENT.IRRIGATION_EVENT_DELETE]: ZoneProperties & {
        event_id: string;
        duration_ms: number;
        duration_bucket: DurationBucket;
        creation_method: IrrigationCreationMethod;
    };
    [ANALYTICS_EVENT.IRRIGATION_EVENT_UPDATE]: ZoneProperties &
        Partial<RecurrenceProperties> & {
            duration_ms: number;
            duration_bucket: DurationBucket;
            creation_method: IrrigationCreationMethod;
            event_id: string;
        };
    [ANALYTICS_EVENT.IRRIGATION_EVENT_UPDATE_FAIL]: ZoneProperties & {
        creation_method: IrrigationCreationMethod;
        error_code: AnalyticsErrorCode;
        event_id: string;
    };

    // Irrigation sets
    [ANALYTICS_EVENT.IRRIGATION_SET_SAVE]: {
        irrigation_set_id?: string;
        affected_event_count?: number;
        zone_count: number;
    };
    [ANALYTICS_EVENT.IRRIGATION_SET_SAVE_FAIL]: {
        irrigation_set_id?: string;
        zone_count: number;
    };

    // Zone
    [ANALYTICS_EVENT.ZONE_EDIT_CLICK]: { zone_id: string };

    // Add device — device fields optional until registration succeeds
    [ANALYTICS_EVENT.DEVICE_REGISTRATION_COMPLETE]: Partial<DeviceProperties>;
    [ANALYTICS_EVENT.DEVICE_REGISTRATION_FAIL]: Partial<DeviceProperties> & {
        error_code: AnalyticsErrorCode;
    };

    // Frill
    [ANALYTICS_EVENT.RELEASE_ANNOUNCEMENT_OPEN]: Record<string, never>;
    [ANALYTICS_EVENT.RELEASE_ANNOUNCEMENT_CLOSE]: Record<string, never>;

    // Irrigation report
    [ANALYTICS_EVENT.IRRIGATION_REPORT_DOWNLOAD]: { report_type: "report_table" | "executed_irrigations" };
};

export type AnalyticsEventName = keyof AnalyticsEventProperties;

/** Sole posthog.capture wrapper — call sites go through here so names/properties match the catalog. */
export function trackEvent<TName extends AnalyticsEventName>(name: TName, properties: AnalyticsEventProperties[TName]) {
    if (!posthogEnabled) return;
    posthog.capture(name, properties);
}
